import React, { useState, useRef, useEffect } from 'react';
import Input from '../../../components/Input/Input';
import { FaTimes, FaUpload } from 'react-icons/fa';
import './AddSocialMediaModal.css';
import { BASE_URL } from '../../../utils/configConstants';

const getMediaUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BASE_URL}${path.replace(/^\//, '')}`;
};

const AddSocialMediaModal = ({
  showModal,
  isEditMode,
  selectedItem,
  saveLoading,
  onSave,
  onClose,
}) => {
  const [socialType, setSocialType] = useState('instagram');
  const [socialUrl, setSocialUrl] = useState('');
  const [socialThumbnail, setSocialThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailError, setThumbnailError] = useState('');
  const thumbnailInputRef = useRef(null);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  useEffect(() => {
    if (showModal) {
      if (isEditMode && selectedItem) {
        setSocialType(selectedItem.social_type || selectedItem.type || 'instagram');
        setSocialUrl(selectedItem.url || '');
        if (selectedItem.thumbnail) {
          setThumbnailPreview(getMediaUrl(selectedItem.thumbnail));
        } else {
          setThumbnailPreview(null);
        }
      } else {
        setSocialType('instagram');
        setSocialUrl('');
        setSocialThumbnail(null);
        setThumbnailPreview(null);
        setThumbnailError('');
      }
    }
  }, [showModal, isEditMode, selectedItem]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
        setThumbnailError('Please select an image file');
        return;
    }

    setThumbnailError('');
    setSocialThumbnail(file);
    const reader = new FileReader();
    reader.onload = (event) => setThumbnailPreview(event.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (!socialType) {
        alert('Please select social type');
        return;
    }
    if (!socialUrl.trim()) {
        alert('Please enter social URL');
        return;
    }

    if (socialType === 'instagram' && !socialThumbnail && !thumbnailPreview) {
        setThumbnailError('Thumbnail is required for Instagram');
        return;
    }

    const formData = new FormData();
    formData.append('type', socialType);
    formData.append('url', socialUrl.trim());
    if (socialThumbnail) {
        formData.append('thumbnail', socialThumbnail);
    }
    onSave(formData);
  };

  if (!showModal) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-form">
          <div className="modal-header-inline">
            <h3 className="modal-title">{isEditMode ? 'Edit Social Media' : 'Add Social Media'}</h3>
            <button type="button" className="modal-close-boxy" onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>
          </div>

          <div className="social-inputs-container">
            <div className="form-group-custom">
              <label className="input-label-custom">Social Type</label>
              <select 
                className="select-field-custom" 
                value={socialType} 
                onChange={(e) => setSocialType(e.target.value)}
              >
                <option value="instagram">Instagram</option>
                <option value="youtube">YouTube</option>
              </select>
            </div>

            <div className="form-group-custom">
              <label className="input-label-custom">Social URL</label>
              <input
                type="text"
                className="input-field-custom"
                placeholder="Enter URL"
                value={socialUrl}
                onChange={(e) => setSocialUrl(e.target.value)}
              />
            </div>

            {socialType === 'instagram' && (
              <div className="social-image-upload-section">
                <label className="input-label-custom">Thumbnail <span style={{ color: 'red' }}>*</span></label>
                <div 
                  className={`social-image-upload-container ${thumbnailError ? 'error-border' : ''}`} 
                  onClick={() => thumbnailInputRef.current?.click()}
                >
                  {thumbnailPreview ? (
                    <img src={thumbnailPreview} className="social-image-preview" alt="Preview" />
                  ) : (
                    <div className="upload-placeholder">
                      <FaUpload className="upload-icon" />
                      <p>Upload Thumbnail</p>
                    </div>
                  )}
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
                {thumbnailError && <p className="error-text">{thumbnailError}</p>}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button className="social-save-btn" onClick={handleSave} disabled={saveLoading}>
              {saveLoading ? 'Saving...' : (isEditMode ? 'Update' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSocialMediaModal;
